import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  DialogContentText,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbox } from 'react-modal-image';

// eslint-disable-next-line no-restricted-imports -- pre-existing EnhancedTable usage; migrate off when touched
import EnhancedTable from '@sophocles/components/EnhancedTable';
import FileDropZone from '@sophocles/components/FileDropZone/FileDropZone';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { formatBytes } from '@sophocles/utils/formatUtils';

// Filled multiline inputs vertically center short text in a tall (rows={6})
// slot by default; anchor typed text to the top instead.
const multilineCommentSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

function EditImageDialog({ data, open, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [file, setFile] = useState([data, null]);
  const [comment, setComment] = useState(data.comment || '');

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(file);
    }
  };

  return (
    <ResponsiveDialog
      title={t('projectServices.workForm.editImage', 'Edit Image')}
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      sx={{ '& .MuiDialogContent-root > :not(style)': { mb: 2 } }}
    >
      <DialogContentText>
        {t(
          'projectServices.workForm.editImageInstructions',
          'Make your edits to this image, and then submit to save your changes.',
        )}
      </DialogContentText>
      {!file && (
        <FileDropZone
          config={{ accept: 'image/jpeg, image/png', maxFiles: 1 }}
          onFilesAdded={uploadedFiles => {
            uploadedFiles.forEach((fileData, index) => {
              const f = fileData[0];
              const d = fileData[1];
              setFile([[f, d]]);
            });
          }}
          onFileRemoved={i => {
            setFile(null);
          }}
        >
          {({ isBusy }) => {
            return (
              <Grow in={!isBusy}>
                <Typography variant="body1">
                  {t('projectServices.workForm.dragDropSelectFiles', 'Drag & drop or click to select files')}
                </Typography>
              </Grow>
            );
          }}
        </FileDropZone>
      )}
      {file && (
        <List>
          <ListItem sx={{ flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'row',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <img
                src={file[0].image}
                style={{ maxWidth: '480px', height: 'auto', borderRadius: 'var(--radius-md)' }}
              />
            </Box>
            <TextField
              margin="dense"
              label={t('projectServices.workForm.comment', 'Comment')}
              type="text"
              multiline
              rows={6}
              fullWidth
              value={comment}
              onChange={e => setComment(e.target.value)}
              sx={multilineCommentSx}
            />
          </ListItem>
        </List>
      )}
    </ResponsiveDialog>
  );
}

function AddNewDialog({ onSubmit }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = e => {
    if (onSubmit) {
      onSubmit(files);
    }
  };

  const removeFile = index => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  return (
    <Fragment>
      <Tooltip title={t('projectServices.workForm.addNew', 'Add New')}>
        <IconButton onClick={handleClickOpen}>
          <AddIcon />
        </IconButton>
      </Tooltip>
      <ResponsiveDialog
        title={t('projectServices.workForm.uploadImages', 'Upload Images')}
        open={open}
        onSubmit={handleSubmit}
        onClose={handleClose}
        sx={{ '& .MuiDialogContent-root > :not(style)': { mb: 2 } }}
      >
        <DialogContentText>
          {t(
            'projectServices.workForm.addNewImageInstructions',
            'To add a new image, please enter all required details here.',
          )}
        </DialogContentText>
        <FileDropZone
          config={{ accept: 'image/jpeg, image/png' }}
          onFilesAdded={uploadedFiles => {
            uploadedFiles.forEach((fileData, index) => {
              const f = fileData[0];
              const d = fileData[1];
              setFiles(currentFiles => currentFiles.concat([[f, d]]));
            });
          }}
          onFileRemoved={i => {
            const newFiles = [...files];
            newFiles.splice(i, 1);
            setFiles(newFiles);
          }}
        >
          {({ isBusy }) => {
            return (
              <Grow in={!isBusy}>
                <Typography variant="body1">
                  {t('projectServices.workForm.dragDropSelectFiles', 'Drag & drop or click to select files')}
                </Typography>
              </Grow>
            );
          }}
        </FileDropZone>
        <List>
          {files.map((x, i) => (
            <ListItem key={x[0].name} sx={{ flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'row',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <ListItemIcon>
                  <UploadFileIcon />
                </ListItemIcon>
                <ListItemText primary={x[0].name} secondary={formatBytes(x[0].size)} />
                <IconButton size="small" onClick={() => removeFile(i)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                margin="dense"
                label={t('projectServices.workForm.comment', 'Comment')}
                type="text"
                multiline
                rows={6}
                fullWidth
                sx={multilineCommentSx}
              />
            </ListItem>
          ))}
        </List>
      </ResponsiveDialog>
    </Fragment>
  );
}

function ImageTable({ title, images, editable }) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightBoxOpen, setLightBoxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const openLightBox = rowIndex => {
    setSelectedIndex(rowIndex);
    setLightBoxOpen(true);
  };

  const handleEdit = image => {
    if (editable) {
      setSelectedImage(image);
      setIsEditDialogOpen(true);
    }
  };

  const generateLightboxImageList = images =>
    images.map(image => ({
      src: image.image,
      caption: 'Test - ' + image.comment || '',
    }));

  const columns = [
    {
      id: 'upload_time',
      label: t('projectServices.workForm.columnTime', 'Time'),
      style: { width: '200px' },
      sortable: true,
      cellRenderer: (datum, row, rowIndex) => moment(datum).format('L, LTS'),
    },
    {
      id: 'image',
      label: t('projectServices.workForm.columnImage', 'Image'),
      style: { width: '175px' },
      sortable: false,
      cellRenderer: (datum, row, rowIndex) => (
        <img className="hover-div" src={datum} width="175" height="125" onClick={() => openLightBox(rowIndex)} />
      ),
    },
    {
      id: 'upload_user',
      label: t('projectServices.workForm.columnUser', 'User'),
      sortable: false,
      style: { minWidth: '100px' },
      cellRenderer: (datum, row, rowIndex) => datum?.name ?? t('projectServices.workForm.unknown', 'Unknown'),
    },
    {
      id: 'comment',
      label: t('projectServices.workForm.comment', 'Comment'),
      sortable: false,
      style: { minWidth: '100px' },
      cellRenderer: (datum, row, rowIndex) => datum || t('projectServices.workForm.none', 'None'),
    },
  ];

  const imagesWithTime = images
    .map(image => {
      return produce(image, draft => {
        draft.moment = _.get(image, 'upload_time', '');
      });
    })
    .sort(image => _.get(image, 'moment'))
    .reverse();

  const lightBoxImages = generateLightboxImageList(imagesWithTime);

  return (
    <Fragment>
      {lightBoxOpen && (
        <Lightbox
          medium={lightBoxImages[selectedIndex].src}
          large={lightBoxImages[selectedIndex].src}
          onClose={() => setLightBoxOpen(false)}
        />
      )}
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <EnhancedTable
          title={title}
          columns={columns}
          rows={imagesWithTime}
          editable={editable}
          addNewElement={<AddNewDialog onSubmit={values => console.log(values)} />}
          onEdit={handleEdit}
        />
        {selectedImage && editable && (
          <EditImageDialog
            open={isEditDialogOpen}
            data={selectedImage}
            onClose={() => {
              setIsEditDialogOpen(false);
              setSelectedImage(null);
            }}
          />
        )}
      </Box>
    </Fragment>
  );
}

export default ImageTable;
